import { ReactElement, useEffect } from 'react';
import {
  StatusBar,
  Input,
  PrimaryButton,
  Spinner,
  Typography,
  SnackbarStatus,
  LinkButton,
} from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { useMenu } from 'src/hooks/useMenu/useMenu';
import { ISection } from 'src/types/menu.types';
import { createNewMenuSection } from 'src/api/createNewMenuSection/createNewMenuSection';
import { updateMenuSection } from 'src/api/updateMenuSection/updateMenuSection';
import { CheckboxList } from '../CheckboxList/CheckboxList';
import { useMealtimes } from 'src/hooks/useMealtimes/useMealtimes';
import { IMealtime } from 'src/types/mealtimes.types';
import { updateMealtimes } from './updateMealtimes/updateMealtimes';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { ButtonContainer } from 'src/components/ButtonContainer/ButtonContainer';
import styles from './SectionTrayContent.module.scss';

export interface ISectionFormTypes {
  name: string;
  description: string;
  items: Record<string, boolean>;
  parent_category_id: string | null;
  mealtimes: Record<string, boolean>;
}

export interface SectionTrayContentProps {
  menuId: string;
  parentSectionId?: string | null;
  section?: ISection;
  onClose: () => void;
  openId: number;
  onSaved?: (sectionId: string, isNew: boolean) => void;
}

const getInitialItems = (section: ISection): Record<string, boolean> => {
  const initialItems = {};
  section.items.forEach((item) => {
    initialItems[item.itemId] = true;
  });
  return initialItems;
};

const getInitialMealtimes = (sectionId: string, mealtimes: IMealtime[]) => {
  const initialMealtimes = {};
  mealtimes.forEach((mealtime) => {
    const isSectionInMealtime = !!mealtime.categories.find((m) => m.category_id === sectionId);
    initialMealtimes[mealtime.mealtime_id] = isSectionInMealtime;
  });
  return initialMealtimes;
};

export const SectionTrayContent = ({
  menuId,
  parentSectionId = null,
  section,
  onClose,
  openId,
  onSaved,
}: SectionTrayContentProps): ReactElement => {
  const queryClient = useQueryClient();
  const { data: menu } = useMenu(menuId);
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { data: mealtimes, isLoading: isMealtimesLoading } = useMealtimes(menuId);

  const {
    handleSubmit,
    formState: { isSubmitting, errors },
    register,
    setValue,
    watch,
    reset,
  } = useForm<ISectionFormTypes>({
    defaultValues: {
      name: section?.name || '',
      description: section?.description || '',
      items: section ? getInitialItems(section) : {},
      parent_category_id: parentSectionId,
      mealtimes: {},
    },
  });

  useEffect(() => {
    reset({
      name: section?.name || '',
      description: section?.description || '',
      items: section ? getInitialItems(section) : {},
      parent_category_id: parentSectionId,
      mealtimes: {},
    });
    clearError();
  }, [openId, reset, clearError, parentSectionId, section]);

  useEffect(() => {
    if (mealtimes && section) {
      setValue('mealtimes', getInitialMealtimes(section.sectionId, mealtimes));
    }
  }, [mealtimes, section, setValue]);

  const parentSection = menu?.sections.find((menuSection) => menuSection.sectionId === parentSectionId);

  const addableItems = [
    ...(section ? section.items : []),
    ...(menu?.uncategorised ?? []),
    ...(parentSection?.items || []),
  ];

  const onSubmit = async (data: ISectionFormTypes) => {
    clearError();
    const items = Object.keys(data.items).filter((item) => data.items[item]);
    const sectionRequest = () => {
      if (section) {
        return updateMenuSection(menuId, section.sectionId, {
          name: data.name,
          description: data.description,
          items,
        });
      }
      return createNewMenuSection(menuId, {
        name: data.name,
        description: data.description,
        items,
        parent_section_id: data.parent_category_id,
      });
    };
    try {
      const sectionRes = await sectionRequest();
      const sectionJson = await sectionRes.json();
      const sectionId = sectionJson.data[0].section_id;

      await updateMealtimes(menuId, sectionId, data.mealtimes, mealtimes ?? []);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });

      onSaved?.(sectionId, !section);
      onClose();
    } catch (e) {
      showNotification({
        status: SnackbarStatus.ERROR,
        message: `Something went wrong whilst ${section ? 'updating' : 'creating'} the menu section`,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles['content']}>
      <div className={styles['scroll-container']}>
        <Input
          type="text"
          label="Name"
          hasMargin
          register={register('name', { required: 'Please enter a menu section name' })}
          error={errors?.name?.message}
        />

        <Input type="text" label="Description" rows={2} hasMargin register={register('description')} />

        {parentSectionId && (
          <div className={styles['parent-category']}>
            <Typography weight="semi-bold">Main section:</Typography>
            <Typography>{parentSection?.name}</Typography>
          </div>
        )}
        <div className={styles['checkbox-containers']}>
          {mealtimes?.length > 0 && !parentSectionId && (
            <div>
              <Typography weight="semi-bold">Mealtimes</Typography>
              {isMealtimesLoading ? (
                <div className={styles['spinner-container']}>
                  <Spinner size="medium" />
                </div>
              ) : (
                <CheckboxList
                  register={register}
                  watch={watch}
                  storagePrefix="mealtimes"
                  setValue={setValue}
                  checkboxes={mealtimes.map((mealtime) => ({ id: mealtime.mealtime_id, label: mealtime.name }))}
                />
              )}
            </div>
          )}

          {addableItems?.length > 0 && (
            <div>
              <Typography weight="semi-bold">Select products to include</Typography>
              <CheckboxList
                register={register}
                watch={watch}
                storagePrefix="items"
                setValue={setValue}
                checkboxes={addableItems.map((item) => ({ id: item.itemId, label: item.name }))}
              />
            </div>
          )}
        </div>
      </div>

      <div className={styles['button-row']}>
        <div className={styles['error-banner']}>
          {errorMessage && (
            <div className={styles['error-banner']}>
              <StatusBar message={errorMessage} variant="error" />
            </div>
          )}
        </div>
        <ButtonContainer
          direction="column-reverse"
          margin="none"
          left={
            <LinkButton type="button" onClick={onClose}>
              Close
            </LinkButton>
          }
          right={
            <PrimaryButton
              size="full-width"
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting || isMealtimesLoading}
            >
              {section ? 'Save' : 'Create'}
            </PrimaryButton>
          }
        />
      </div>
    </form>
  );
};

export default SectionTrayContent;
